// src/data/schoolLockerSubmissions.ts
// -----------------------------------
// Repository for the `school_locker_submissions` table — the audit trail of
// School Locker order submissions (the idempotency check + the sent/failed
// records). Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type SchoolLockerSubmissionRow =
  Database["public"]["Tables"]["school_locker_submissions"]["Row"];
type SchoolLockerSubmissionInsert =
  Database["public"]["Tables"]["school_locker_submissions"]["Insert"];

// The "sent" submission for a request, if any — the idempotency guard + the UI's
// already-submitted status. Null if none / error / no client.
export async function getSentSubmissionByRequestId(
  requestId: number | string | null | undefined
): Promise<SchoolLockerSubmissionRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("school_locker_submissions")
    .select("*")
    .eq("request_id", requestId as number)
    .eq("status", "sent")
    .maybeSingle();
  if (error) {
    logger.warn("getSentSubmissionByRequestId failed:", error.message);
    return null;
  }
  return (data as unknown as SchoolLockerSubmissionRow) ?? null;
}

// Record a School Locker submission attempt (status "sent" or "failed").
export async function insertSubmission(
  row: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("school_locker_submissions")
    .insert(row as SchoolLockerSubmissionInsert);
  if (error) logger.warn("insertSubmission failed:", error.message);
  return { error };
}
